import React from 'react';
import PropTypes from 'prop-types';
import { Select, styled } from '@mui/material';
import { checkboxClasses } from '@mui/material/Checkbox';
import { iconButtonClasses } from '@mui/material/IconButton';
import InputLabel, { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import { typographyClasses } from '@mui/material/Typography';

// Mode-aware (igual que StyledTextfield/StyledPassword): en OSCURO label/borde gris claro
// + texto blanco; en CLARO label/borde NEGRO marcado + texto negro, para que los SELECT
// (filtros de admin/entrenador, Género, Estado, Moneda, etc.) no queden "abandonados"
// sobre la card blanca. Foco/hover en dorado en ambos modos.
const LIGHT_LABEL = 'rgba(17, 21, 27, 0.82)';
const LIGHT_BORDER = 'rgba(17, 21, 27, 0.6)';
const LIGHT_ICON = 'rgba(17, 21, 27, 0.7)';
const DARK_NEUTRAL = '#E3E7EB';

export const StyledInputLabel = styled(InputLabel)(({ theme }) => {
  const labelColor = theme.palette.mode === 'light' ? LIGHT_LABEL : DARK_NEUTRAL;

  return {
    [`&.${inputLabelClasses.root}`]: {
      color: labelColor,
      [`&.${inputLabelClasses.focused}`]: {
        color: theme.palette.secondary.main
      },
      [`&.${inputLabelClasses.error}`]: {
        color: '#F6B1B1'
      },
      [`&.${inputLabelClasses.disabled}`]: {
        color: theme.palette.action.disabled,
        '.MuiInputLabel-asterisk': {
          color: theme.palette.action.disabled
        }
      }
    },
    [`&.${inputLabelClasses.asterisk}`]: {
      color: labelColor,
      '&.Mui-error': {
        color: '#F6B1B1'
      }
    }
  };
});

const BaseStyledSelect = styled(Select)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const borderColor = isLight ? LIGHT_BORDER : DARK_NEUTRAL;
  const textColor = isLight ? theme.palette.text.primary : theme.palette.common.white;
  const iconColor = isLight ? LIGHT_ICON : DARK_NEUTRAL;

  return {
    [`&.${outlinedInputClasses.root}`]: {
      textAlign: 'left',
      color: textColor,
      [theme.breakpoints.up('lg')]: {
        height: '47px'
      },
      '& fieldset': {
        border: `1px solid ${borderColor}`
      },
      '&:hover fieldset': {
        borderColor: theme.palette.secondary.main
      },
      [`&.${outlinedInputClasses.focused}`]: {
        '& fieldset': {
          borderColor: theme.palette.secondary.main
        },
        [`& .${iconButtonClasses.root}`]: {
          color: theme.palette.secondary.main
        }
      },
      [`&.${outlinedInputClasses.disabled}`]: {
        '& fieldset': {
          borderColor: theme.palette.action.disabled
        },
        [`& .${iconButtonClasses.root}`]: {
          color: theme.palette.action.disabled
        }
      }
    },
    [`& .${outlinedInputClasses.input}`]: {
      color: textColor
    },
    [`& .${iconButtonClasses.root}`]: {
      color: iconColor
    }
  };
});

// Wrapper para agregar MenuProps por defecto
export function StyledSelect({ MenuProps = {}, ...otherProps }) {
  return (
    <BaseStyledSelect
      {...otherProps}
      MenuProps={{
        ...MenuProps,
        PaperProps: {
          ...MenuProps?.PaperProps,
          sx: {
            ...MenuProps?.PaperProps?.sx,
            maxWidth: '500px !important',
            maxHeight: '400px !important'
          }
        }
      }}
    />
  );
}

StyledSelect.propTypes = {
  MenuProps: PropTypes.object
};

// Ítem seleccionado: fondo dorado + texto OSCURO (ink) en ambos modos. Antes usaba
// gray2.main, que en claro es casi blanco → texto claro sobre dorado (ilegible).
export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  [`&.${menuItemClasses.selected}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.ink.main,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.ink.main
    },
    '&:focus': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.ink.main
    }
  }
}));

export const StyledMultipleMenuItem = styled(MenuItem)(({ theme }) => ({
  [`&.${menuItemClasses.selected}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.ink.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.ink.main
    },
    '&:focus': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.ink.main
    },
    [`& .${typographyClasses.root}`]: {
      fontWeight: 600
    },
    [`& .${checkboxClasses.root}.${checkboxClasses.checked}`]: {
      color: theme.palette.ink.main
    }
  }
}));
